// @owners { team: patients }
import { Body, Card, ExternalLink, LgPadding, LoadingEmptyState, getBrand } from '@alto/design-system';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getCompanyPhone } from '~shared/config/getCompanyPhone';
import { COMMS_CHANNEL } from '~shared/constants';
import { getURLParams } from '~shared/helpers/helper';
import { stripNonDigits } from '~shared/helpers/string';
import { useMarkWrongUser } from '~shared/queries/query-keys/verifications';
import { BackLink } from '~web/components/BackLink';
import { PageBackground, SingleColumnContentWrapper } from '~web/components/Page';

type NotMeRouteParams = {
  readonly verificationType: 'email' | 'phone';
};

function pageText(commsChannel: string, result: boolean | null | undefined) {
  if (result) {
    return <Body>{COMMS_CHANNEL[commsChannel as keyof typeof COMMS_CHANNEL]} successfully removed.</Body>;
  }

  const companyPhone = getCompanyPhone();
  return (
    <Body>
      Please try again or contact support at{' '}
      <ExternalLink href={`tel:${stripNonDigits(companyPhone)}`}>{companyPhone}</ExternalLink> if the problem persists
    </Body>
  );
}

export const NotMe = () => {
  const { verificationType = '' } = useParams<NotMeRouteParams>();
  const { token } = getURLParams();
  const { mutateAsync: markWrongUser, isPending: loading } = useMarkWrongUser();
  const [isMarkedWrongUser, setIsMarkedWrongUser] = useState(false);
  // Marking "not me" is a one-shot, externally-visible action — guard so a re-run of the
  // effect (e.g. a mutateAsync reference change) can't repeat the call for the same link.
  const firedForRef = useRef<string | null>(null);

  useEffect(() => {
    const key = `${verificationType}:${token}`;
    if (firedForRef.current === key) {
      return;
    }
    firedForRef.current = key;
    markWrongUser({ token, commsChannel: verificationType })
      .then((marked) => {
        setIsMarkedWrongUser(marked);
        return marked;
      })
      .catch(() => {
        setIsMarkedWrongUser(false);
      });
  }, [markWrongUser, token, verificationType]);

  if (loading) {
    return <LoadingEmptyState />;
  }

  return (
    <PageBackground>
      <SingleColumnContentWrapper>
        <Card>
          <LgPadding>
            <BackLink
              href={`https://${getBrand()}.com`}
              text={`Return to ${getBrand()}.com`}
            />
            {pageText(verificationType, isMarkedWrongUser)}
          </LgPadding>
        </Card>
      </SingleColumnContentWrapper>
    </PageBackground>
  );
};
